import * as React from 'react';
import classnames from 'classnames';
import { Modal, LockBodyScroll, List, Button } from '@grandvisionhq/common';
import { Heading, Text } from '@grandvisionhq/elements';
import { CheckboxWithLabel } from '@grandvisionhq/forms';
import { useIntl } from '@grandvisionhq/state';
import { vtoEvent, loadDitto } from '../../utils.js';
import { useVto } from '../../../context/VtoProvider.js';

var VtoCreationStep;
(function(VtoCreationStep) {
    VtoCreationStep[VtoCreationStep["INTRO"] = 0] = "INTRO";
    VtoCreationStep[VtoCreationStep["CREATION"] = 1] = "CREATION";
})(VtoCreationStep || (VtoCreationStep = {}));
const DittoModal = ({ onCreate , onCancel , onError , skipIntro , sku , vtoBanner ,  })=>{
    const { config  } = useVto();
    const intl = useIntl();
    const { accessKey , apiUrl , isEnabled , sdkUrl , getSignature , partnerId  } = config;
    const [hasAgreed, setAgreement] = React.useState(false);
    const [activeStep, setActiveStep] = React.useState(skipIntro ? VtoCreationStep.CREATION : VtoCreationStep.INTRO);
    const [showErrorMessage, setShowErrorMessage] = React.useState(false);
    const [errorCode, setErrorCode] = React.useState(null);
    const imgUrl = vtoBanner === null || vtoBanner === void 0 ? void 0 : vtoBanner.image.url;
    React.useEffect(()=>{
        switch(activeStep){
            case VtoCreationStep.INTRO:
                return vtoEvent('vto.creation.view.intro', {
                    sku
                });
            case VtoCreationStep.CREATION:
                return vtoEvent('vto.creation.view.creation', {
                    sku
                });
        }
    }, [
        activeStep
    ]);
    React.useEffect(()=>{
        let isCanceled = false;
        if (!isEnabled || activeStep !== VtoCreationStep.CREATION) {
            return;
        }
        let instance;
        Promise.all([
            loadDitto(sdkUrl),
            getSignature(partnerId)
        ]).then(([ditto, clientSignature])=>{
            if (!isCanceled) {
                instance = new ditto.Scan({
                    tryOnServer: apiUrl,
                    domSelector: '.ditto-modal__target',
                    accessKey,
                    partnerSignature: clientSignature,
                    partnerId,
                    font: 'nunitosans'
                }, {
                    success: (response)=>{
                        vtoEvent('vto.creation.success', {
                            sku
                        });
                        onCreate(response.scanId);
                    },
                    close: onCancel,
                    progress: ({ status  })=>{
                        switch(status){
                            case 'INIT_CAMERA':
                                vtoEvent('vto.creation.view.camera', {
                                    sku
                                });
                                break;
                            case 'READY':
                                vtoEvent('vto.creation.view.creation', {
                                    sku
                                });
                                break;
                            case 'RECORD':
                                vtoEvent('vto.creation.view.record', {
                                    sku
                                });
                                break;
                            case 'UPLOAD_STARTED':
                                vtoEvent('vto.creation.view.save', {
                                    sku
                                });
                                break;
                            default:
                                return;
                        }
                    },
                    failure: ({ status , description  })=>{
                        setErrorCode(status);
                        vtoEvent('vto.creation.error', {
                            sku,
                            status,
                            description
                        });
                    }
                });
            }
        }).catch(onError);
        return ()=>{
            isCanceled = true;
            instance && instance.destroy();
        };
    }, [
        activeStep
    ]);
    const onButtonClick = ()=>{
        if (hasAgreed) {
            return setActiveStep(VtoCreationStep.CREATION);
        }
        setShowErrorMessage(true);
    };
    return /*#__PURE__*/ React.createElement(Modal, {
        activeTrap: !skipIntro,
        "data-t": "ditto-modal",
        isOpen: true,
        className: classnames('ditto-modal__modal', {
            'ditto-modal__modal--image': imgUrl
        }),
        showIcon: activeStep === VtoCreationStep.CREATION && errorCode !== 'CAMERA_ACCESS_DENIED' ? false : true,
        onModalClose: onCancel
    }, /*#__PURE__*/ React.createElement(LockBodyScroll, null), /*#__PURE__*/ React.createElement("div", {
        className: "ditto-modal"
    }, activeStep === VtoCreationStep.INTRO && renderPrivacy(), activeStep === VtoCreationStep.CREATION && errorCode !== 'CAMERA_ACCESS_DENIED' && /*#__PURE__*/ React.createElement("div", {
        className: "ditto-modal__target"
    }), errorCode && errorCode === 'CAMERA_ACCESS_DENIED' && /*#__PURE__*/ React.createElement("div", {
        className: "ditto-modal__error-message"
    }, intl.getLabel('vto.errors.cameraAccessDenied'))));
    function renderPrivacy() {
        return /*#__PURE__*/ React.createElement(React.Fragment, null, imgUrl && /*#__PURE__*/ React.createElement("div", {
            className: "ditto-modal__aside",
            style: {
                backgroundImage: `url(${imgUrl})`
            }
        }), /*#__PURE__*/ React.createElement("div", {
            className: classnames('ditto-modal__main', {
                'ditto-modal__main--with-error': showErrorMessage && !hasAgreed
            })
        }, /*#__PURE__*/ React.createElement("div", {
            className: "ditto-modal__titles"
        }, /*#__PURE__*/ React.createElement(Heading, {
            variant: "small",
            className: "ditto-modal__title"
        }, intl.getLabel('vto.title')), /*#__PURE__*/ React.createElement(Heading, {
            variant: "smallest",
            className: "ditto-modal__subtitle"
        }, intl.getLabel('vto.subtitle'))), /*#__PURE__*/ React.createElement(List, {
            className: "ditto-modal__usps",
            items: [
                intl.getLabel('vto.step.1'),
                intl.getLabel('vto.step.2'),
                intl.getLabel('vto.step.3'),
                intl.getLabel('vto.step.4'), 
            ],
            numerical: true
        }), /*#__PURE__*/ React.createElement(Text, {
            className: "ditto-modal__description",
            color: "text-secondary",
            variant: "caption"
        }, intl.getLabel('vto.description')), /*#__PURE__*/ React.createElement(CheckboxWithLabel, {
            id: "ditto-modal-opt-in",
            className: "ditto-modal__opt-in",
            onChange: ()=>setAgreement(!hasAgreed)
            ,
            checked: hasAgreed,
            value: "ditto-modal",
            "data-t": "ditto-modal-checkbox",
            label: intl.getFormattedLabel('vto.optIn', {
                parseHTML: true
            }),
            error: showErrorMessage && !hasAgreed,
            errorMessage: showErrorMessage && !hasAgreed ? intl.getLabel('vto.errors.terms') : ''
        }), /*#__PURE__*/ React.createElement(Button, {
            caption: intl.getLabel('vto.actions.start'),
            onClick: onButtonClick,
            "data-t": "ditto-modal-button"
        })));
    }
};

export { DittoModal };
//# sourceMappingURL=DittoModal.js.map
